import { React, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  ModalFooter,
  Button,
  Label,
  Popover,
  PopoverHeader,
  PopoverBody,
} from "reactstrap";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import { ColumnMenu } from "../components/kendoComponents/columnMenu/ColumnMenu";
import { GridPager } from "../components/kendoComponents/gridPager/GridPager";
import { ColumnProps } from "../components/kendoComponents/columnProps/ColumnProps";
import { useNavigate } from "react-router-dom";
import LoaderSpin from "../components/loaderSpin/LoaderSpin";
import PropTypes from "prop-types";
import moment from "moment";
import useAuth from "../components/authGuard/useAuth";


import {
  DropDownButton,
  DropDownButtonItem,
} from "@progress/kendo-react-buttons";

import "../assets/scss/kendo-theme-custom.scss";
import {
  fetchLastProcessList,
  setFilterProcessType,
  getSelectedProcess,
  fetchNewRunParameters,
  newRun,
  checkCanLaunchNewRun,
} from "../store/app/ProcessRuns/ProcessRunsSlice";

import DateCell from "../components/kendoComponents/dateCell/DateCell";
import { setFilterIntervallo } from "../store/app/DataQualityStatus/DataQualitySlice";

const getTooltipItems = (dataItem) => {
  const tooltipItems = Array.isArray(dataItem?.tooltip)
    ? dataItem.tooltip.filter(
        (item) =>
          item.id !== "Documentazione funzionale" &&
          item.id !== "Documentazione tecnica"
      )
    : [];

  return tooltipItems;
};

const DetailsCell = (props) => {
  const { dataItem } = props;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const tooltipItems = getTooltipItems(dataItem);
  const targetId = `process-details-${dataItem.process_id}`;

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setPopoverOpen(false);
      closeTimerRef.current = null;
    }, 150);
  };

  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  useEffect(() => {
    if (!popoverOpen) return undefined;
    const handleScroll = () => {
      clearCloseTimer();
      setPopoverOpen(false);
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [popoverOpen]);

  if (tooltipItems.length === 0) {
    return <td />;
  }

  return (
    <td className="text-center align-middle">
      <Button
        id={targetId}
        color="link"
        className="process-details-button"
        aria-label={`Show details for ${dataItem.name}`}
        onMouseEnter={() => { clearCloseTimer(); setPopoverOpen(true); }}
        onMouseLeave={scheduleClose}

      >
        <i className="bi bi-info-circle"></i>
      </Button>
      <Popover
        placement="right"
        isOpen={popoverOpen}
        target={targetId}
        toggle={() => setPopoverOpen(false)}
        className="process-details-popover"
      >
        <PopoverHeader className="py-2">Process details</PopoverHeader>
        <PopoverBody
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
        >
          <ul className="process-details-list">
            {tooltipItems.map((item) => (
              <li key={item.id}>
                <strong>{item.id}: </strong>
                {item.description || "N/A"}
              </li>
            ))}
          </ul>
        </PopoverBody>
      </Popover>
    </td>
  );
};

DetailsCell.propTypes = {
  dataItem: PropTypes.object.isRequired,
};

const ValidationModal = (props) => {
  const { modal, validationData, toggleModal, handleContinue } = props;

  const getChildrenVersionList = () => {
    const list = validationData?.children_version_list;

    if (list === null || list === undefined) return null;

    const stringValue = String(list).trim();
    return stringValue.length > 0 ? stringValue : null;
  };

  const hasChildrenVersionList = () => {
    return getChildrenVersionList() !== null;
  };
  
  return (
    <Modal isOpen={modal} size="md">
      <ModalHeader toggle={toggleModal}>
        <h5 className="pt-2 fw-bold">
          {validationData?.is_blocking ? "Warning - Action Blocked" : "Notice - Proceed with Caution"}
        </h5>
      </ModalHeader>
      <ModalBody>
        <p>
          <strong>Notice:</strong> Descendant valid version(s) exist</p>
        {hasChildrenVersionList() && (
          <p>
            <strong>Affected versions: </strong>
            {getChildrenVersionList()}
          </p>
        )}
        {validationData?.is_blocking && (
          <p className="text-danger fw-bold">
            Please open a ticket to resolve this issue.
          </p>
        )}
      </ModalBody>
      <ModalFooter className="border-primary justify-content-between">
        <Button
          color="primary"
          outline
          className="text-uppercase"
          onClick={toggleModal}
        >
          {validationData?.is_blocking ? "Close" : "Cancel"}
        </Button>
        {!validationData?.is_blocking && (
          <Button
            color="primary"
            className="text-uppercase"
            onClick={handleContinue}
          >
            Continue
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export const NewRunModal = (props) => {
  const { modal, newRunParameters, target, toggleModal, handleOk } = props;
  const [parameters, setParameters] = useState([]);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setParameters(newRunParameters);
    const initialValues = newRunParameters.reduce((acc, param) => {
      acc[param.name] = param.type === "boolean" ? "Yes" : "";
      return acc;
    }, {});
    setValues(initialValues);
  }, [newRunParameters]);

  const handleInputChange = (name, value, originalValue) => {
    setValues({
      ...values,
      [name]: value,
      [name + "_originalValue"]: originalValue,
    });
  };

  const handleSubmit = () => {
    const newErrors = {};
    parameters.forEach((param) => {
      if (!param.is_optional && !values[param.name]) {
        newErrors[param.name] = "This field is required";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = parameters
      .filter((param) => !(param.is_optional && values[param.name] === ""))
      .map((param) => ({
        name: param.name,
        value: param.type === "boolean" ? values[param.name] === "Yes" : values[param.name],
        type: param.type,
      }));

    // Find refDate from the parameters (assuming it's named 'refDate' or similar)
    const refDateParam = parameters.find(param =>
      param.name.toLowerCase().includes('refdate') ||
      param.name.toLowerCase().includes('ref_date') ||
      param.name.toLowerCase().includes('datarifermento') ||
      param.type === 'date'
    );

    // Get the original date value (YYYY-MM-DD format) instead of the formatted one
    const refDate = refDateParam ? values[refDateParam.name + "_originalValue"] : null;

    handleOk(payload, refDate);
  };

  const getInputType = (param) => {
    switch (param.type) {
      case "date":
        return (
          <Input
            type="date"
            id={`param-${param.name}`}
            placeholder="-"
            value={values[`${param.name}_originalValue`] || ""}
            onChange={(event) =>
              handleInputChange(
                param.name,
                moment(event.target.value).format("YYYY-MM-DD"),
                event.target.value
              )
            }
          />
        );
      case "boolean":
        return (
          <Input
            type="select"
            id={`param-${param.name}`}
            value={values[param.name] || ""}
            onChange={(e) =>
              handleInputChange(param.name, e.target.value, e.target.value)
            }
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </Input>
        );
      default:
        return (
          <Input
            type="text"
            id={`param-${param.name}`}
            value={values[param.name] || ""}
            onChange={(e) =>
              handleInputChange(param.name, e.target.value, e.target.value)
            }
          />
        );
    }
  };

  return (
    <Modal isOpen={modal} size="xl">
      <ModalHeader className="" toggle={toggleModal}>
        <h5 className="pt-2 fw-bold">New Run</h5>
      </ModalHeader>
      <ModalBody>
        {target && (
          <div className="mb-3 p-2 bg-light-primary border border-primary rounded">
            <div className="small text-muted mb-1 fw-bold text-uppercase">Target</div>
            <Row className="g-2">
              {target.name && (
                <Col xs="12" md="6">
                  <div className="small text-muted">Process</div>
                  <div className="fw-bold text-truncate" title={target.name}>
                    {target.name}
                  </div>
                </Col>
              )}
              {target.process_type_name && (
                <Col xs="12" md="6">
                  <div className="small text-muted">Process Type</div>
                  <div className="fw-bold text-truncate" title={target.process_type_name}>
                    {target.process_type_name}
                  </div>
                </Col>
              )}
              {target.output_type_name && (
                <Col xs="12" md="6">
                  <div className="small text-muted">Data Product</div>
                  <div className="fw-bold text-truncate" title={target.output_type_name}>
                    {target.output_type_name}
                  </div>
                </Col>
              )}
              {target.current_revision_number != null && (
                <Col xs="12" md="6">
                  <div className="small text-muted">Revision</div>
                  <div className="fw-bold">{target.current_revision_number}</div>
                </Col>
              )}
            </Row>
          </div>
        )}
        <Form>
          <div className="form-body">
            <Row>
              {parameters.length === 0 ? (
                <Col>
                  <p className="text-warning">
                    There are no editable parameters for re-executing
                    this run, do you want to proceed anyway?
                  </p>
                </Col>
              ) : (
                parameters.map((param) => (
                  <Col key={`param-group-${param.name}`} md={6} sm={12}>
                    <FormGroup>
                      <Label
                        for={`param-${param.name}`}
                        title={param.description?.trim() ? param.description : undefined}
                      >
                        {param.name}{" "}
                        {!param.is_optional && (
                          <span className="text-danger">*</span>
                        )}
                      </Label>
                      {getInputType(param)}
                      {errors[param.name] && (
                        <div className="text-danger">{errors[param.name]}</div>
                      )}
                    </FormGroup>
                  </Col>))
              )}
            </Row>
          </div>
        </Form>
      </ModalBody>
      <ModalFooter className="border-primary justify-content-between">
        <Button
          color="primary"
          outline
          className="text-uppercase"
          onClick={toggleModal}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          className="text-uppercase"
          onClick={() => handleSubmit()}
        >
          Confirm
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const MenuCell = (props) => {

  const { goToLatestRuns, openModalNewRun, canRunForType } = props;


  const disabled =
    props.dataItem.prefect_deployment == null ||
    !canRunForType(props.dataItem.output_type_id);
  return (
    <td>
      <DropDownButton
        buttonClass="border-0"
        iconClass="k-icon k-i-menu"
        popupClass="p-3"
        rounded={null}
        ariaLabel="Actions"
        ariaRole="button"
        onItemClick={(event) => {
          if (
            event.item.text ==
            "/process-selection/latest-runs/" +
            props.dataItem.process_id
          ) {
            goToLatestRuns(
              props.dataItem.process_id,
              props.dataItem.process_type_name
            );
          } else {
            openModalNewRun(
              props.dataItem.process_id,
              props.dataItem.current_revision_number,
              props.dataItem
            );
          }
        }}
      >
        <DropDownButtonItem
          props={props}
          text={
            "/process-selection/latest-runs/" +
            props.dataItem.process_id
          }
          render={() => <li>Latest runs</li>}
        />
        <DropDownButtonItem
          props={props}
          text={
            "/process-selection/new-run/" +
            props.dataItem.process_id
          }
          disabled={disabled}
          render={() => <li>New Run</li>}
        />
      </DropDownButton>
    </td>
  );

};

MenuCell.propTypes = {
  dataItem: PropTypes.object.isRequired,
  canRunForType: PropTypes.func.isRequired,
  goToLatestRuns: PropTypes.func.isRequired,
  openModalNewRun: PropTypes.func.isRequired,
};


ValidationModal.propTypes = {
  modal: PropTypes.bool.isRequired,
  validationData: PropTypes.object,
  toggleModal: PropTypes.func.isRequired,
  handleContinue: PropTypes.func.isRequired,
};

NewRunModal.propTypes = {
  modal: PropTypes.bool.isRequired,
  newRunParameters: PropTypes.array.isRequired,
  target: PropTypes.object,
  toggleModal: PropTypes.func.isRequired,
  handleOk: PropTypes.func.isRequired,
};

const ProcessSelection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const { isAdmin } = useAuth();

  const dataCatalogOwnership = useSelector(
    (state) => state.dataQualityReducer.dataCatalogOwnership
  );

  const refreshCount = useSelector(
    (state) => state.dataQualityReducer.refreshCount
  );
  const searchParams = new URLSearchParams(location.search);
  const lastProcessList = useSelector(
    (state) => state.processRunsReducer.lastProcessList
  );
  const newRunParameters = useSelector(
    (state) => state.processRunsReducer.newRunParameters
  );

  const isSpinnerVisible = useSelector(
    (state) => state.processRunsReducer.loading
  );

  const [dataState, setDataState] = useState({
    sort: [
      {
        field: "code",
        dir: "asc",
      },
    ],
    skip: 0,
    take: 20,
  });

  const [dataResult, setDataResult] = useState(
    process(lastProcessList, dataState)
  );

  const [modal, setModal] = useState(false);
  const [validationModal, setValidationModal] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [selectedRow, setSelectedRow] = useState({});
  const [newRunTarget, setNewRunTarget] = useState(null);
  const [pendingParameters, setPendingParameters] = useState(null);

  useEffect(() => {
    dispatch(fetchLastProcessList());
  }, [refreshCount, navigate]);

  useEffect(() => {
    setDataState(() => ({
      sort: [
        {
          field: "code",
          dir: "asc",
        },
      ],
      skip: 0,
      take: 20,
    }));
    setDataResult(process(lastProcessList, dataState));
  }, [lastProcessList]);

  const goToLatestRuns = (processId, processTypeName) => {
    dispatch(setFilterIntervallo(744));
    dispatch(setFilterProcessType([processTypeName]));
    dispatch(
      getSelectedProcess({
        processId: processId,
        processTypeName: processTypeName,
      })
    );

    searchParams.set("interval", JSON.stringify(744));
    searchParams.set("process", JSON.stringify([processTypeName]));

    navigate(
      "/process-selection/latest-runs/" +
      processId +
      "?" +
      searchParams.toString()
    );
  };

  const openModalNewRun = (processId, revisionNum, targetItem) => {
    dispatch(fetchNewRunParameters(processId, revisionNum));
    setSelectedRow({ processId: processId, revisionNum: revisionNum });
    setNewRunTarget(targetItem || null);
    setModal(true);
  };

  const toggleModal = () => {
    setModal(!modal);
  };

  const toggleValidationModal = () => {
    setValidationModal(!validationModal);
  };

  const handleValidationContinue = () => {
    toggleValidationModal();
    toggleModal();
    dispatch(newRun(selectedRow, pendingParameters));
    setPendingParameters(null);
  };

  const canRunForType = (outputTypeId) => {
    if (isAdmin) return true;
    return dataCatalogOwnership.some(
      (item) =>
        item.type_id === outputTypeId &&
        (item.user_role?.toLowerCase() === "owner" ||
          item.user_role?.toLowerCase() === "delegate")
    );
  };

  const getFilteredRunParameters = () => {
    if (isAdmin) return newRunParameters;
    // Only show ref date and replace version (replace version field must be boolean)
    return newRunParameters.filter(
      (param) =>
        param.type === "date" ||
        param.name.toLowerCase().includes("refdate") ||
        param.name.toLowerCase().includes("ref_date") ||
        param.name.toLowerCase().includes("replace_version")
    ).map((param) => {
      if (param.type === "boolean") {
        return {
          ...param,
          options: ["No", "Yes"],
        };
      }
      return param;
    });
  };

  const prefectCall = (parameters, refDate) => {
    // Check validation before calling newRun

    dispatch(checkCanLaunchNewRun(selectedRow.processId, refDate))
      .then((validationResult) => {

        if (!validationResult) {
          return; // Error already handled in checkCanLaunchNewRun
        }

        const hasWarnings =
          validationResult.is_blocking ||
          (validationResult.children_version_list != null &&
            String(validationResult.children_version_list).trim().length > 0);

        if (!hasWarnings) {
          // No validation issues, proceed directly
          toggleModal();
          dispatch(newRun(selectedRow, parameters));
          return;
        }

        // Store data and parameters for validation
        setValidationData(validationResult);
        setPendingParameters(parameters);

        // Show validation modal directly
        setValidationModal(true);
      });
  };

  const renderMenuCell = (props) => {

    return <MenuCell {...props} goToLatestRuns={goToLatestRuns} openModalNewRun={openModalNewRun}
      canRunForType={canRunForType} />;
  };

  return (
    <>
      <Container fluid className="py-2 px-4  position-relative">
        <Row>
          <Col>
            <div className="grid_task_container border">
              <Grid
                style={{ height: "100%" }}
                pageable={true}
                sortable={true}
                total={dataResult?.length}
                data={dataResult}
                {...dataState}
                pager={(props) => GridPager(props, lastProcessList)}
                onDataStateChange={(e) => {
                  setDataState(e.dataState);
                  setDataResult(process(lastProcessList, e.dataState));
                }}
              >
                <Column
                  title=""
                  width="50"
                  cell={DetailsCell}
                />
                <Column
                  {...ColumnProps("name", dataState)}
                  title="Name"
                  columnMenu={ColumnMenu}
                />
                <Column
                  {...ColumnProps("process_type_name", dataState)}
                  title="Process Type"
                  columnMenu={ColumnMenu}
                />
                <Column
                  {...ColumnProps("current_revision_number", dataState)}
                  title="Current Rev."
                  columnMenu={ColumnMenu}
                />
                <Column
                  {...ColumnProps("current_revision_date", dataState)}
                  title="Release Date"
                  columnMenu={ColumnMenu}
                  cell={(props) => DateCell(props, false)}
                />
                <Column
                  {...ColumnProps("output_type_name", dataState)}
                  title="Data Product"
                  columnMenu={ColumnMenu}
                />
                <Column
                  title=" "
                  width="60"
                  cell={renderMenuCell} />
              </Grid>
            </div>
          </Col>
        </Row>
        <LoaderSpin isVisible={isSpinnerVisible} />
      </Container>

      <ValidationModal
        modal={validationModal}
        validationData={validationData}
        toggleModal={toggleValidationModal}
        handleContinue={handleValidationContinue}
      />

      <NewRunModal
        modal={modal}
        newRunParameters={getFilteredRunParameters()}
        target={newRunTarget}
        toggleModal={toggleModal}
        handleOk={prefectCall}
      />
    </>
  );
};

export default ProcessSelection;